import React from 'react';

import { Box, Divider, styled, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import { DateFormatPatterns, DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatMoney, numberWithCommas, sum } from '@clearverify/utils';

import { EmploymentDataPayrollItemInfoFragment } from 'src/api';

export type EmploymentDataHistoricalIncomeBlockProps = {
  payrollItems: Nullable<EmploymentDataPayrollItemInfoFragment[]>;
};

export const EmploymentDataHistoricalIncomeBlock: React.FC<
  EmploymentDataHistoricalIncomeBlockProps
> = ({ payrollItems }) => {
  if (!payrollItems) return null;

  return (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
      <StyledTable>
        <thead>
          <tr>
            <Typography component="th" variant="body1">
              Check Date
            </Typography>
            <Typography component="th" variant="body1">
              Hours
            </Typography>
            <Typography component="th" variant="body1">
              Gross
            </Typography>
            <Typography component="th" variant="body1">
              Regular
            </Typography>
            <Typography component="th" variant="body1">
              Overtime
            </Typography>
            <Typography component="th" variant="body1">
              Bonus
            </Typography>
            <Typography component="th" variant="body1">
              Commission
            </Typography>
            <Typography component="th" variant="body1">
              Sick
            </Typography>
            <Typography component="th" variant="body1">
              Vacation
            </Typography>
            <Typography component="th" variant="body1">
              Misc.
            </Typography>
            <Typography component="th" variant="body1">
              Other
            </Typography>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={11}>
              <Divider />
            </td>
          </tr>

          {payrollItems.map(payrollItem => (
            <tr key={payrollItem.id}>
              <Typography component="td">
                {payrollItem.paymentDate
                  ? DateTime.fromISO(payrollItem.paymentDate).toFormat(
                      DateFormatPatterns.shortDateWithSlash,
                    )
                  : DEFAULT_VALUE_IF_NIL}
              </Typography>
              <Typography component="td">
                {numberWithCommas(sum(payrollItem.grossHours, payrollItem.overtimeHours))}
              </Typography>
              <Typography component="td">{formatMoney(payrollItem.grossWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.baseWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.overtimeWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.bonusesWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.commissionWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.sickWage)}</Typography>
              <Typography component="td">{formatMoney(payrollItem.vacationWage)}</Typography>
              <Typography component="td">
                {formatMoney(sum(payrollItem.miscWage, payrollItem.otherWage))}
              </Typography>
              <Typography component="td">
                {formatMoney(
                  sum(
                    payrollItem.holidayWage,
                    payrollItem.pension,
                    payrollItem.severance,
                    payrollItem.tips,
                    payrollItem.incentiveWage,
                    payrollItem.ptoWage,
                    payrollItem.ptoPayoutWage,
                    payrollItem.sickTimePayoutWage,
                    payrollItem.vacationTimePayoutWage,
                  ),
                )}
              </Typography>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </Box>
  );
};

const StyledTable = styled('table')(style => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0 1rem',

  '& tr > *:not(:last-child)': {
    padding: 0,
    paddingRight: style.theme.spacing(2),
  },

  '& th': {
    textAlign: 'left',
    color: style.theme.palette.text.secondary,
  },
}));
